import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Paper, Typography, Chip, CircularProgress,
  List, ListItem, ListItemText, Divider, Link, Grid, Accordion,
  AccordionSummary, AccordionDetails, Badge, Checkbox, Pagination,
  Collapse, Autocomplete, FormControl, InputLabel, Select, MenuItem,
  ToggleButton, ToggleButtonGroup, useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import { searchSenateQon } from '../services/api';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import ExportCSVButton from './ExportCSVButton';
import RSSButton from './RSSButton';
import { SaveSearchButton } from './saved-searches';
import { sortFacetsBooleansFirst } from '../utils/facetOrder';

export default function SenateQonSearch() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedMetadata, setExpandedMetadata] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  // #262: Sort order. `_score` (default, relevance), `asked_date:desc` (newest),
  // `asked_date:asc` (oldest). Backend accepts `field:dir` shape.
  const [sortOrder, setSortOrder] = useState(searchParams.get('sort') || '_score');

  // Date drill-down state
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const [availableOptions, setAvailableOptions] = useState({
    askedBy: [],
    askedTo: [],
    portfolios: []
  });
  const [advancedForm, setAdvancedForm] = useState({
    keywords: '',
    askedBy: '',
    askedTo: '',
    portfolio: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  // #262: examples must use fields that actually exist on the index — the
  // older `senator:` and `parliament:` examples errored the backend
  // query_string (default_field:"*") and surfaced as "Search failed".
  const examples = [
    'asked_by:"Penny Wong"',
    'part:"Treasury"',
    'status:"Answered"',
    'asked_date:[2024-01-01 TO *]'
  ];

  useEffect(() => {
    const urlQuery = searchParams.get('q');
    const urlFilters = searchParams.get('filters');

    if (urlQuery) {
      const parsedFilters = urlFilters ? JSON.parse(urlFilters) : {};
      setActiveFilters(parsedFilters);
      handleSearch(urlQuery, 1, parsedFilters);
    } else {
      handleSearch('*', 1, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Extract options from facets when results change
  useEffect(() => {
    if (results?.facets) {
      const newOptions = { ...availableOptions };
      if (results.facets.asked_by) {
        const askedBy = results.facets.asked_by.map(f => f.value).filter(Boolean).sort();
        if (askedBy.length > 0) newOptions.askedBy = [...new Set([...newOptions.askedBy, ...askedBy])].sort();
      }
      if (results.facets.asked_to) {
        const askedTo = results.facets.asked_to.map(f => f.value).filter(Boolean).sort();
        if (askedTo.length > 0) newOptions.askedTo = [...new Set([...newOptions.askedTo, ...askedTo])].sort();
      }
      if (results.facets.parts) {
        const portfolios = results.facets.parts.map(f => f.value).filter(Boolean).sort();
        if (portfolios.length > 0) newOptions.portfolios = [...new Set([...newOptions.portfolios, ...portfolios])].sort();
      }
      setAvailableOptions(newOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) {
      return String(baseQuery || '*');
    }

    const filterQuery = filterParts.join(' AND ');
    if (baseQuery && baseQuery !== '*') {
      return `(${baseQuery}) AND ${filterQuery}`;
    }
    return filterQuery;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = null, year = selectedYear, month = selectedMonth, pageSize = perPage, sort = sortOrder) => {
    const queryToUse = typeof searchQuery === 'string' ? searchQuery : String(query || '*');
    const filtersToUse = filters !== null && typeof filters === 'object' && !filters.nativeEvent ? filters : activeFilters;

    const fullQuery = buildQueryWithFilters(queryToUse, filtersToUse);

    if (!fullQuery || fullQuery.trim() === '') {
      return;
    }

    const urlParams = { q: queryToUse };
    if (Object.keys(filtersToUse).length > 0) {
      urlParams.filters = JSON.stringify(filtersToUse);
    }
    if (pageSize !== DEFAULT_PAGE_SIZE) urlParams.pageSize = pageSize;
    if (sort && sort !== '_score') urlParams.sort = sort;
    setSearchParams(urlParams);

    setLoading(true);
    try {
      const data = await searchSenateQon(fullQuery, newPage, pageSize, year, month, sort);
      setResults(data);
      setPage(newPage);
    } catch (error) {
      console.error('Search error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Search failed. Check console for details.';
      alert(`Search failed: ${errorMsg}`);
    }
    setLoading(false);
  };

  // #262: Sort dropdown handler.
  const handleSortChange = (newSort) => {
    setSortOrder(newSort);
    handleSearch(query, 1, activeFilters, selectedYear, selectedMonth, perPage, newSort);
  };

  // Date drill-down handlers
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, year, null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth(month);
    setSelectedDay(null);
    const year = month.split('-')[0];
    setSelectedYear(year);
    handleSearch(null, 1, activeFilters, year, month);
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, activeFilters, null, null);
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(query, newPage, activeFilters);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(query, 1, activeFilters, selectedYear, selectedMonth, newSize);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) {
      newFilters[field] = [];
    }

    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) {
        delete newFilters[field];
      }
    } else {
      newFilters[field].push(value);
    }

    setActiveFilters(newFilters);
    handleSearch(query, 1, newFilters);
  };

  const removeFilter = (field, value) => {
    handleFacetClick(field, value);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(query, 1, {});
  };

  const isFilterActive = (field, value) => {
    return activeFilters[field]?.includes(value) || false;
  };

  const facetToField = (facetName) => {
    const mapping = {
      'asked_by': 'asked_by',
      'asked_to': 'asked_to',
      'statuses': 'status',
      'parts': 'part'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'asked_by': 'Asked By',
      'asked_to': 'Asked To',
      'statuses': 'Status',
      'parts': 'Portfolio'
    };
    return names[facetName] || facetName.replace('_', ' ');
  };

  const toggleQuestionExpansion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const toggleMetadataExpansion = (questionId) => {
    setExpandedMetadata(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (advancedForm.keywords?.trim()) {
      parts.push(advancedForm.keywords.trim());
    }
    if (advancedForm.askedBy?.trim()) {
      parts.push(`asked_by:"${advancedForm.askedBy.trim()}"`);
    }
    if (advancedForm.askedTo?.trim()) {
      parts.push(`asked_to:"${advancedForm.askedTo.trim()}"`);
    }
    if (advancedForm.portfolio?.trim()) {
      parts.push(`part:"${advancedForm.portfolio.trim()}"`);
    }
    if (advancedForm.status?.trim()) {
      parts.push(`status:"${advancedForm.status.trim()}"`);
    }
    if (advancedForm.dateFrom || advancedForm.dateTo) {
      const from = advancedForm.dateFrom || '*';
      const to = advancedForm.dateTo || '*';
      parts.push(`asked_date:[${from} TO ${to}]`);
    }

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const handleAdvancedSearch = () => {
    const advQuery = buildAdvancedQuery();
    setQuery(advQuery);
    setActiveFilters({});
    handleSearch(advQuery, 1, {});
  };

  // #262: site-wide Reset — wipes query, filters, advanced form, drill-down,
  // page, sort, and re-runs the default '*' search. Matches the
  // Parliamentarians Reset behaviour per CLAUDE.md "Reset stays site-wide".
  const resetAll = () => {
    setQuery('');
    setActiveFilters({});
    setAdvancedForm({
      keywords: '',
      askedBy: '',
      askedTo: '',
      portfolio: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setPage(1);
    setPerPage(DEFAULT_PAGE_SIZE);
    setSortOrder('_score');
    handleSearch('*', 1, {}, null, null, DEFAULT_PAGE_SIZE, '_score');
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Senate Questions on Notice</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder='Try: asked_by:"Penny Wong" or part:"Treasury"'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={resetAll} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Examples:
            </Typography>
            {examples.map((ex, i) => (
              <Chip
                key={i}
                label={ex}
                size="small"
                onClick={() => setQuery(ex)}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={advancedForm.keywords}
                onChange={(e) => setAdvancedForm({...advancedForm, keywords: e.target.value})}
                placeholder="Search in question text..."
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.askedBy}
                value={advancedForm.askedBy}
                onChange={(e, v) => setAdvancedForm({...advancedForm, askedBy: v || ''})}
                renderInput={(params) => <TextField {...params} label="Asked By (Senator)" />}
                freeSolo
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.askedTo}
                value={advancedForm.askedTo}
                onChange={(e, v) => setAdvancedForm({...advancedForm, askedTo: v || ''})}
                renderInput={(params) => <TextField {...params} label="Asked To (Minister)" />}
                freeSolo
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.portfolios}
                value={advancedForm.portfolio}
                onChange={(e, v) => setAdvancedForm({...advancedForm, portfolio: v || ''})}
                renderInput={(params) => <TextField {...params} label="Portfolio" />}
                freeSolo
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={advancedForm.status}
                  onChange={(e) => setAdvancedForm({...advancedForm, status: e.target.value})}
                  label="Status"
                >
                  <MenuItem value="">Any</MenuItem>
                  <MenuItem value="Answered">Answered</MenuItem>
                  <MenuItem value="Unanswered">Unanswered</MenuItem>
                  <MenuItem value="Transferred">Transferred</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Date From"
                type="date"
                value={advancedForm.dateFrom}
                onChange={(e) => setAdvancedForm({...advancedForm, dateFrom: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Date To"
                type="date"
                value={advancedForm.dateTo}
                onChange={(e) => setAdvancedForm({...advancedForm, dateTo: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleAdvancedSearch}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={resetAll} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {results && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {results.facets && Object.keys(results.facets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {(Object.keys(activeFilters).length > 0 || selectedYear) && (
                    <Button size="small" onClick={() => { clearAllFilters(); clearDateDrillDown(); }} sx={{ minWidth: 'auto', fontSize: '0.75rem' }}>
                      Clear All
                    </Button>
                  )}
                </Box>

                {/* Date Hierarchy Filter */}
                {(results.facets?.years?.length > 0 || results.facets?.months?.length > 0 || results.facets?.days?.length > 0) && (
                  <DateHierarchyFilter
                    facets={results.facets}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedDay={selectedDay}
                    onYearClick={handleYearClick}
                    onMonthClick={handleMonthClick}
                    onDayClick={handleDayClick}
                    onClearAll={clearDateDrillDown}
                  />
                )}

                {/* Active Filters */}
                {Object.keys(activeFilters).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Active Filters:
                    </Typography>
                    {Object.entries(activeFilters).map(([field, values]) =>
                      values.map((value) => (
                        <Chip
                          key={`${field}-${value}`}
                          label={value}
                          size="small"
                          onDelete={() => removeFilter(field, value)}
                          sx={{ mr: 0.5, mb: 0.5, fontSize: '0.75rem' }}
                        />
                      ))
                    )}
                  </Box>
                )}

                {sortFacetsBooleansFirst(['asked_by', 'asked_to', 'parts', 'statuses'], results.facets).map((facetName) => (
                  results.facets[facetName] && results.facets[facetName].length > 0 && (
                    <Accordion key={facetName} defaultExpanded={facetName === 'statuses'}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          {facetDisplayName(facetName)}
                          {activeFilters[facetToField(facetName)]?.length > 0 && (
                            <Badge
                              badgeContent={activeFilters[facetToField(facetName)].length}
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {results.facets[facetName].slice(0, 15).map((facet) => (
                            <ListItem
                              key={facet.value}
                              button
                              onClick={() => handleFacetClick(facetToField(facetName), facet.value)}
                              sx={{ px: 0, py: 0.5 }}
                            >
                              <Checkbox
                                edge="start"
                                checked={isFilterActive(facetToField(facetName), facet.value)}
                                size="small"
                                sx={{ py: 0 }}
                              />
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {facet.value || '(empty)'}
                                    </Typography>
                                    <Chip
                                      label={facet.count}
                                      size="small"
                                      sx={{ height: 20, fontSize: '0.75rem' }}
                                    />
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          {/* Results Panel */}
          <Grid item xs={12} md={results.facets && Object.keys(results.facets).length > 0 ? 9 : 12}>
            {/* #262: Std two-row results header. Row 1 = count (left, normal
                weight) + page-nav (right). Row 2 = PageSize · Sort · Export
                · Save · RSS. Mirrors Parliamentarians' layout. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Found {results.total.toLocaleString()} results ({results.took_ms}ms)
                </Typography>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel id="senateqon-sort-label">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SortIcon fontSize="small" />
                      Sort by
                    </Box>
                  </InputLabel>
                  <Select
                    labelId="senateqon-sort-label"
                    value={sortOrder}
                    onChange={(e) => handleSortChange(e.target.value)}
                    label="Sort by"
                  >
                    <MenuItem value="_score">Relevance</MenuItem>
                    <MenuItem value="asked_date:desc">Newest first</MenuItem>
                    <MenuItem value="asked_date:asc">Oldest first</MenuItem>
                  </Select>
                </FormControl>
                <ExportCSVButton
                  dataType="senate-qon"
                  query={query || '*'}
                  totalResults={results.total}
                />
                <SaveSearchButton
                  searchType="senate_qon"
                  queryText={query || '*'}
                  parameters={{ sort: sortOrder, page, per_page: perPage, ...activeFilters }}
                  resultCount={results.total}
                />
                <RSSButton collection="senate-qon" query={query || '*'} />
              </Box>
            </Box>

            <List>
              {results.hits.map((hit, index) => (
                <React.Fragment key={hit.id}>
                  <ListItem alignItems="flex-start" sx={{ px: 0, flexDirection: 'column' }}>
                    <ListItemText
                      sx={{ width: '100%' }}
                      primary={
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            {/* #262: Title now links to the Q&A detail page,
                                not the asker's bio. Senator name moves to
                                the byline below as an inline link. */}
                            <Typography
                              variant="resultTitle"
                              component={RouterLink}
                              to={`/senateqon/${hit.id}`}
                              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              Q{hit.question_number || ''}{hit.title ? ` - ${hit.title}` : ''}
                            </Typography>
                            {hit.status && (
                              <Chip
                                label={hit.status}
                                color={hit.status === 'Answered' ? 'success' : 'warning'}
                                size="small"
                              />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }} component="div">
                            {hit.asked_by && (
                              <>
                                Asked by:{' '}
                                {hit.asked_by_id ? (
                                  <RouterLink to={`/parliamentarians/${hit.asked_by_id}`} style={{ color: theme.palette.ledger.accentText, textDecoration: 'none' }}>
                                    {hit.asked_by}
                                  </RouterLink>
                                ) : (
                                  hit.asked_by
                                )}
                              </>
                            )}
                            {[
                              hit.asked_to && `To: ${hit.asked_to}`,
                              hit.part && `Portfolio: ${hit.part}`,
                              hit.asked_date && `Date: ${hit.asked_date}`
                            ].filter(Boolean).map((s, i) => (i > 0 || hit.asked_by ? ` · ${s}` : s))}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <>
                          {/* Question Content */}
                          {(hit.content || hit.content_html) && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Question:
                              </Typography>
                              {hit.content_html ? (
                                <Typography
                                  className="ps-snippet"
                                  variant="body2"
                                  component="div"
                                  sx={{
                                    color: 'text.primary',
                                    '& ol, & ul': { pl: 2, my: 1 },
                                    '& li': { mb: 0.5 }
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: expandedQuestions[hit.id]
                                      ? hit.content_html
                                      : hit.content_html.substring(0, 500) + (hit.content_html.length > 500 ? '...' : '')
                                  }}
                                />
                              ) : (
                                <Typography className="ps-snippet" variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                                  {expandedQuestions[hit.id] ? hit.content : hit.content?.substring(0, 300)}
                                  {!expandedQuestions[hit.id] && hit.content?.length > 300 && '...'}
                                </Typography>
                              )}
                              {(hit.content?.length > 300 || hit.content_html?.length > 500) && (
                                <Button
                                  size="small"
                                  onClick={() => toggleQuestionExpansion(hit.id)}
                                  sx={{ textTransform: 'none', fontSize: '0.875rem', p: 0, ml: 0.5 }}
                                >
                                  {expandedQuestions[hit.id] ? 'Show less' : 'Show more'}
                                </Button>
                              )}
                            </Box>
                          )}

                          {/* Expanded Metadata */}
                          {expandedMetadata[hit.id] && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 0.5, mb: 1, bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                              {hit.question_number && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Question Number:</Typography>
                                  <Typography variant="body2">{hit.question_number}</Typography>
                                </>
                              )}
                              {hit.title && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Title:</Typography>
                                  <Typography variant="body2">{hit.title}</Typography>
                                </>
                              )}
                              {hit.asked_by && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Asked By:</Typography>
                                  {hit.asked_by_id ? (
                                    <RouterLink to={`/parliamentarians/${hit.asked_by_id}`} style={{ color: theme.palette.ledger.accentText, textDecoration: 'none' }}>
                                      {hit.asked_by}
                                    </RouterLink>
                                  ) : (
                                    <Typography variant="body2">{hit.asked_by}</Typography>
                                  )}
                                </>
                              )}
                              {hit.asked_to && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Asked To:</Typography>
                                  <Typography variant="body2">{hit.asked_to}</Typography>
                                </>
                              )}
                              {hit.part && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Portfolio:</Typography>
                                  <Typography variant="body2">{hit.part}</Typography>
                                </>
                              )}
                              {hit.asked_date && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Date Asked:</Typography>
                                  <Typography variant="body2">{hit.asked_date}</Typography>
                                </>
                              )}
                              {hit.status && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Status:</Typography>
                                  <Typography variant="body2">{hit.status}</Typography>
                                </>
                              )}
                              {hit.source && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Source:</Typography>
                                  <Typography variant="body2">{hit.source}</Typography>
                                </>
                              )}
                              {hit.source_url && (
                                <>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Source URL:</Typography>
                                  <Link href={hit.source_url} target="_blank" rel="noopener" variant="body2">
                                    View on ParlWork
                                  </Link>
                                </>
                              )}
                            </Box>
                          )}

                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              onClick={() => toggleMetadataExpansion(hit.id)}
                              sx={{ textTransform: 'none', fontSize: '0.875rem', p: 0, minWidth: 'auto' }}
                            >
                              {expandedMetadata[hit.id] ? 'Hide metadata' : 'Show all metadata'}
                            </Button>
                            {hit.source_url && (
                              <Link
                                href={hit.source_url}
                                target="_blank"
                                rel="noopener"
                                sx={{ fontWeight: 500, ml: 2 }}
                              >
                                View on ParlWork
                              </Link>
                            )}
                          </Box>
                        </>
                      }
                    />
                  </ListItem>
                  {index < results.hits.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            {results.total > perPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(results.total / perPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
